import { useEffect } from 'react';
import { useQueryClient, useJobStatus } from 'shared-ui';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';

import { editMultiOwners } from 'api/owners/owners';
import { JobIdResponse } from 'api/jobs/jobs.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useSelectedOwner } from 'hooks/useSelectedOwner/useSelectedOwner';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useModalState } from 'hooks/useModalState/useModalState';

import { EditMultiOwners } from './EditMultiOwners';
import { EditMultiOwnersContainerProps, EditMultiOwnersForm } from './EditMultiOwners.types';
import { RcpOptionsEnum } from './EditMultiOwners.enum';

export const EditMultiOwnersContainer = ({ checkedOwners }: EditMultiOwnersContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { selectedOwner } = useSelectedOwner();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  const handleJobComplete = () => {
    if (selectedOwner && checkedOwners.some(({ id }) => id === selectedOwner?.id)) {
      queryClient.invalidateQueries([QueryKeyEnum.OWNERS]);
    }
    queryClient.invalidateQueries([QueryKeyEnum.OWNERS]);
    toast.dark(formatMessage({ id: AppMessages['owners.multiAction.edit.success'] }));
    onModalClose();
  };

  const handleJobError = (error: string) => {
    toast.error(formatMessage({ id: AppMessages['common.error'] }) || `Error: ${error}`);
  };

  const jobStatus = useJobStatus({
    useGlobalModal: true,
    autoClose: false,
    onComplete: handleJobComplete,
    onError: handleJobError,
    showItemCount: true,
    customCompleteMessage: formatMessage({ id: AppMessages['owners.batchEdit.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['owners.batchEdit.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['owners.batchEdit.result.processing'] }),
    title: formatMessage({ id: AppMessages['owners.batchEdit.progressTitle'] }),
  });

  const { mutate: onEditMultiOwners, isLoading } = useMutation(editMultiOwners, {
    onSuccess: (response: JobIdResponse) => {
      if (!response || !response.jobId) {
        toast.error(formatMessage({ id: AppMessages['common.error'] }));
        return;
      }
      jobStatus.startJob(response.jobId);
      onModalClose();
    },
    onError: (error: unknown) => {
      const errorObj = error as Record<string, unknown>;
      toast.error(
        formatMessage({ id: AppMessages['common.error'] }) || `Error: ${errorObj?.message || 'Unknown error'}`,
      );
    },
  });

  const formMethods = useForm<EditMultiOwnersForm>({
    mode: 'all',
  });

  useEffect(() => {
    formMethods.reset();
  }, [isModalOpen]);

  const onSubmit = (formBody: EditMultiOwnersForm) => {
    const rcp = formBody.rcp === RcpOptionsEnum.TRUE ? true : formBody.rcp === RcpOptionsEnum.FALSE ? false : undefined;

    onEditMultiOwners({
      pkds: checkedOwners.map(({ id }) => id),
      ...formBody,
      rcp,
      komorka: formBody.komorka ? [{ id: formBody.komorka.id, nazwa: formBody.komorka.nazwa }] : undefined,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <EditMultiOwners
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
